/**
 * Full-page layout for screens outside the in-match shell (landing, create, join, lobby,
 * pending, settings, results): safe-area aware header with back button, a centred readable
 * column, and an optional sticky footer for the primary action (thumb zone).
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { goBack } from '../app/router';
import { cx } from './cx';
import { focusHeading } from './focus';
import { Icon } from './Icon';
import styles from './Page.module.css';

interface PageProps {
  title: ReactNode;
  /** Small line above the title, e.g. "Step 1 of 3". */
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  /** Back button target when there is no in-app history. Omit to hide the back button. */
  backTo?: string;
  backLabel?: string;
  /** Right side of the header (e.g. settings button). */
  headerAction?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  width?: 'narrow' | 'wide';
  className?: string;
  /** Hide the title visually (still the page's h1). */
  hideTitle?: boolean;
}

export function Page({
  title,
  eyebrow,
  subtitle,
  backTo,
  backLabel = 'Back',
  headerAction,
  footer,
  children,
  width = 'narrow',
  className,
  hideTitle,
}: PageProps) {
  const h1 = useRef<HTMLHeadingElement>(null);
  // Route changes focus the new h1 (§10) — except on first load, where focus stays at the top.
  useLayoutEffect(() => {
    if (document.body.dataset.wbNavigated === 'true') focusHeading(h1.current);
    document.body.dataset.wbNavigated = 'true';
  }, []);
  return (
    <div className={cx(styles.page, className)}>
      {(backTo || headerAction) && (
        <div className={cx(styles.topBar, width === 'wide' && styles.wide)}>
          {backTo ? (
            <button type="button" className={styles.back} onClick={() => goBack(backTo)}>
              <Icon name="back" size={22} />
              <span>{backLabel}</span>
            </button>
          ) : (
            <span />
          )}
          {headerAction}
        </div>
      )}
      <main id="main" className={cx(styles.main, width === 'wide' && styles.wide)}>
        <header className={cx(styles.header, hideTitle && 'wb-visually-hidden')}>
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h1 ref={h1} tabIndex={-1} className={styles.title}>
            {title}
          </h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </header>
        {children}
      </main>
      {footer && (
        <div className={styles.footerWrap}>
          <div className={cx(styles.footer, width === 'wide' && styles.wide)}>{footer}</div>
        </div>
      )}
    </div>
  );
}
